import { api } from "../ApiState";
import { IApiResponse } from "../../Types/ApiResponse.model";
import {
  TicketDepartmentType,
  TicketPriorityType,
} from "../../Types/Ticket.model";

export const ticketApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTicketsList: builder.query<
      any,
      { search?: object; page?: number; deleted?: boolean }
    >({
      query: ({ search, page, deleted }) => ({
        url: "ticket/list",
        method: "GET",
        params: {
          ...(search && { search: JSON.stringify({ ...search }) }),
          ...(page && { page }),
          ...(deleted && { deleted }),
        },
      }),
      providesTags: ["TicketList"],
    }),

    getTicket: builder.query<IApiResponse<any>, { id: number; page: number }>({
      query: ({ id, page }) => {
        return {
          url: `ticket/show/${id}`,
          method: "GET",
          params: { ...(page && { page }) },
        };
      },
      providesTags: ["Ticket"],
    }),

    storeTicket: builder.mutation<
      IApiResponse<any>,
      {
        title: string;
        department: TicketDepartmentType;
        priority: TicketPriorityType;
        text: string;
        files: string[];
        user_id: number;
      }
    >({
      query: (data) => ({
        url: `ticket/create`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => (result ? [{ type: "TicketList" }] : []),
    }),

    sendMessage: builder.mutation<
      IApiResponse<any>,
      {
        id: number;
        message: {
          text: string;
          files: string[];
        };
      }
    >({
      query: ({ id, message }) => ({
        url: `ticket/reply/${id}`,
        method: "POST",
        body: message,
      }),
    }),

    updateMessage: builder.mutation<
      IApiResponse<any>,
      {
        id: number;
        message: {
          id: number;
          text: string;
          files: string[];
        };
      }
    >({
      query: ({ id, message }) => ({
        url: `ticket/reply/${id}/${message.id}`,
        method: "POST",
        body: { ...message, _method: "PUT" },
      }),
    }),

    sendFile: builder.mutation<
      IApiResponse<any>,
      {
        id: number;
        parent_id: number;
        media: { file: File };
      }
    >({
      query: ({ id, parent_id, media }) => {
        const formData = new FormData();
        formData.append("file", media.file);
        if (parent_id !== undefined) {
          formData.append("parent_id", parent_id.toString());
        }

        return {
          url: `ticket/reply/${id}`,
          method: "POST",
          body: formData,
        };
      },
    }),

    ticketHappiness: builder.mutation<
      IApiResponse<any>,
      { id: string | number; satisfaction: "happy" | "unhappy" }
    >({
      query: ({ id, satisfaction }) => ({
        url: `ticket/modify-ticket-satisfaction/${id}`,
        method: "POST",
        body: { satisfaction: satisfaction, _method: "PUT" },
      }),
    }),

    // delete ticket
    deleteTicket: builder.mutation<IApiResponse<any>, number>({
      query: (id) => ({
        url: `ticket/delete/${id}`,
        method: "POST",
        body: { _method: "DELETE" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "TicketList" }] : []),
    }),

    // pending
    restoreTicket: builder.mutation<IApiResponse<any>, number>({
      query: (id) => ({
        url: `ticket/restore/${id}`,
        method: "POST",
        body: { _method: "PUT" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "TicketList" }] : []),
    }),

    deleteMessage: builder.mutation<
      IApiResponse<any>,
      {
        ticket_id: number;
        message_id: number;
      }
    >({
      query: ({ ticket_id, message_id }) => ({
        url: `ticket/reply/delete/${ticket_id}/${message_id}`,
        method: "POST",
        body: { _method: "DELETE" },
      }),
    }),

    restoreMessage: builder.mutation<
      IApiResponse<any>,
      {
        ticket_id: number;
        message_id: number;
      }
    >({
      query: ({ ticket_id, message_id }) => ({
        url: `ticket/reply/restore/${ticket_id}/${message_id}`,
        method: "POST",
        body: { _method: "PUT" },
      }),
    }),

    transferTicket: builder.mutation<
      IApiResponse<any>,
      {
        ticket_id: number;
        admin_id: number;
      }
    >({
      query: ({ ticket_id, admin_id }) => ({
        url: `ticket/transfer/${ticket_id}`,
        method: "POST",
        body: { admin_id: admin_id },
      }),
      invalidatesTags: (result) => (result ? [{ type: "Ticket" }] : []),
    }),

    // chane department
    changeTicketDepartment: builder.mutation<
      IApiResponse<any>,
      { id: number; department: TicketDepartmentType }
    >({
      query: ({ id, department }) => ({
        url: `ticket/modify-ticket-department/${id}`,
        method: "POST",
        body: {
          _method: "PUT",
          department: department,
        },
      }),
    }),

    // change department
    changeTicketStatus: builder.mutation<
      IApiResponse<any>,
      { id: number; status: "closed" | "waiting" }
    >({
      query: ({ id, status }) => ({
        url: `ticket/modify-ticket-status/${id}`,
        method: "POST",
        body: { _method: "PUT", status: status },
      }),
    }),

    // report section
    getTicketDepartmentReport: builder.query<IApiResponse<any>, void>({
      query: () => ({
        url: "ticket/department-chart",
        method: "GET",
      }),
      transformResponse(baseQueryReturnValue: IApiResponse) {
        return baseQueryReturnValue?.response?.data;
      },
    }),

    getTicketConversationReport: builder.query<IApiResponse<any>, void>({
      query: () => ({
        url: "ticket/conversation-report",
        method: "GET",
      }),
    }),

    getTicketOperatorList: builder.query<
      any,
      { search?: object; page?: number; deleted?: boolean }
    >({
      query: ({ search, page, deleted }) => ({
        url: "ticket/state-operators",
        method: "GET",
        params: {
          ...(search && { search: JSON.stringify({ ...search }) }),
          ...(page && { page }),
          ...(deleted && { deleted }),
        },
      }),
    }),
  }),
});

export const {
  useGetTicketsListQuery,
  useGetTicketQuery,
  useStoreTicketMutation,
  useSendMessageMutation,
  useUpdateMessageMutation,
  useSendFileMutation,
  useTicketHappinessMutation,
  useDeleteTicketMutation,
  useRestoreTicketMutation,
  useDeleteMessageMutation,
  useRestoreMessageMutation,
  useTransferTicketMutation,
  useChangeTicketDepartmentMutation,
  useChangeTicketStatusMutation,
  useGetTicketDepartmentReportQuery,
  useGetTicketConversationReportQuery,
  useGetTicketOperatorListQuery,
} = ticketApi;

export default ticketApi;
